import * as React from 'react';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import compose from 'recompose/compose';
import { withRouter } from 'react-router-dom';
import { closeModal } from 'src/actions/modals';
import { addToastWithTimeout } from 'src/actions/toasts';
import deleteCommunityMutation from 'shared/graphql/mutations/community/deleteCommunity';

import deleteChannelMutation from 'shared/graphql/mutations/channel/deleteChannel';

import deleteThreadMutation from 'shared/graphql/mutations/thread/deleteThread';

import deleteMessage from 'shared/graphql/mutations/message/deleteMessage';

import archiveChannel from 'shared/graphql/mutations/channel/archiveChannel';
import removeCommunityMember from 'shared/graphql/mutations/communityMember/removeCommunityMember';

import ModalContainer from '../modalContainer';
import { PrimaryButton, PrimaryOutlineButton } from 'src/components/button';
import { modalStyles } from '../styles';
import { Actions, Message } from './style';

import dataProvider from 'shared/rest';

/*
  Generic component that should be used to confirm any 'delete' action.
  Takes modalProps as an object with four fields:

  entity => represents the table for lookup in the backend. Currently can
  be either 'thread', 'channel', or 'community'

  id => id of the entity to be deleted

  message => components can construct a custom confirmation message

  redirect => optional => string which represents the path a user should return
  too after deleting a thing (e.g. '/foo/bar')
*/

const DATA_TEST_PRE = 'components-modal-DeleteDoubleCheckModal-';
export const deleteMessageWithToast = (dispatch, deleteMessage, id) => {
  return deleteMessage(id)
    .then(({ data }) => {
      const { deleteMessage } = data;
      if (deleteMessage) {
        dispatch(addToastWithTimeout('neutral', 'Message deleted.'));
      }
    })
    .catch(err => {
      dispatch(
        addToastWithTimeout(
          'error',
          `Sorry, we weren't able to delete this message. ${err.message}`
        )
      );
    });
};

class DeleteDoubleCheckModal extends React.Component {
  state = {
    isLoading: false,
  };

  close = () => {
    this.props.dispatch(closeModal());
  };

  triggerDelete = () => {
    const {
      history,
      modalProps: { id, entity, redirect, extraProps },
      dispatch,
    } = this.props;

    this.setState({
      isLoading: true,
    });
    switch (entity) {
      case 'message':
        return deleteMessageWithToast(
          this.props.dispatch,
          this.props.deleteMessage,
          id
        ).then(() => {
          this.setState({
            isLoading: false,
          });
          this.close();
        });
      case 'thread': {
        if (!extraProps) return;
        const { community } = extraProps.thread;
        return this.props
          .deleteThread(id, extraProps.selectedType)
          .then(({ data }) => {
            const { deleteThread } = data;
            if (deleteThread) {
              history.replace(`/${community.slug}?posts`);
              dispatch(addToastWithTimeout('neutral', 'Post deleted.'));
              this.setState({
                isLoading: false,
              });
              this.close();
            }
            return;
          })
          .catch(err => {
            dispatch(
              addToastWithTimeout(
                'error',
                `Sorry, we weren't able to delete this post. ${err.message}`
              )
            );
          });
      }
      case 'channel': {
        return this.props
          .deleteChannel(id)
          .then(({ data }) => {
            const { deleteChannel } = data;
            if (deleteChannel) {
              // TODO: When we figure out the mutation reducers in apollo
              // client we can just history push and trust the store to update
              // eslint-disable-next-line
              window.location.href = redirect ? redirect : '/';
              // history.push(redirect ? redirect : '/');
              dispatch(addToastWithTimeout('neutral', 'Channel deleted.'));
              this.setState({
                isLoading: false,
              });
              this.close();
            }
            return;
          })
          .catch(err => {
            dispatch(
              addToastWithTimeout(
                'error',
                `Sorry, we weren't able to delete this channel. ${err.message}`
              )
            );
          });
      }
      case 'community': {
        return this.props
          .deleteCommunity(id)
          .then(({ data }) => {
            const { deleteCommunity } = data;
            if (deleteCommunity) {
              // TODO: When we figure out the mutation reducers in apollo
              // client we can just history push and trust the store to update
              // eslint-disable-next-line
              window.location.href = redirect ? redirect : '/';
              // history.push(redirect ? redirect : '/');
              dispatch(addToastWithTimeout('neutral', 'Community deleted.'));
              this.setState({
                isLoading: false,
              });
              this.close();
            }
            return;
          })
          .catch(err => {
            dispatch(
              addToastWithTimeout(
                'error',
                `Sorry, we weren't able to delete this community. ${err.message}`
              )
            );
            this.setState({
              isLoading: false,
            });
          });
      }
      case 'channel-archive': {
        return this.props
          .archiveChannel({ channelId: id })
          .then(() => {
            dispatch(addToastWithTimeout('neutral', 'Channel archived'));
            this.setState({
              isLoading: false,
            });
            return this.close();
          })
          .catch(err => {
            dispatch(addToastWithTimeout('error', err.message));
            this.setState({
              isLoading: false,
            });
          });
      }
      case 'team-member-leaving-community': {
        if (
          this.props.community &&
          this.props.community.communityPermissions.isOwner
        ) {
          dispatch(
            addToastWithTimeout('error', 'Creator can not leave the group!')
          );
          return;
        }
        return this.props
          .removeCommunityMember({ input: { communityId: id } })
          .then(() => {
            dispatch(addToastWithTimeout('neutral', 'Left group'));
            this.setState({
              isLoading: false,
            });
            if (extraProps) {
              extraProps.reloadChairs(true);
            }
            return this.close();
          })
          .catch(err => {
            dispatch(addToastWithTimeout('error', err.message));
            this.setState({
              isLoading: false,
            });
          });
      }
      case 'event': {
        if (!extraProps) return;
        const { id } = extraProps.event;
        return dataProvider
          .invoke('events', 'deleteEvent', { eventId: id })
          .then(res => {
            if (res.data.ok) {
              dispatch(addToastWithTimeout('neutral', 'Event deleted'));
              this.setState({
                isLoading: false,
              });
              history.push(extraProps.url);
            } else {
              dispatch(
                addToastWithTimeout(
                  'error',
                  res.data.message || 'delete failed'
                )
              );
              this.setState({
                isLoading: false,
              });
            }
            return this.close();
          })
          .catch(err => {
            dispatch(addToastWithTimeout('error', err.message));
            this.setState({
              isLoading: false,
            });
          });
      }
      default: {
        this.setState({
          isLoading: false,
        });

        return dispatch(
          addToastWithTimeout(
            'error',
            'Unable to figure out what you wanted to delete. Whoops!'
          )
        );
      }
    }
  };

  render() {
    const {
      isOpen,
      modalProps: { message, buttonLabel },
    } = this.props;
    const styles = modalStyles(540);

    return (
      <Modal
        /* TODO(@mxstbr): Fix this */
        ariaHideApp={false}
        isOpen={isOpen}
        contentLabel={'Are you sure?'}
        onRequestClose={this.close}
        shouldCloseOnOverlayClick={true}
        style={styles}
        closeTimeoutMS={330}
      >
        {/*
          We pass the closeModal dispatch into the container to attach
          the action to the 'close' icon in the top right corner of all modals
        */}
        <ModalContainer title={'Are you sure?'} closeModal={this.close}>
          <Message>{message ? message : 'Are you sure?'}</Message>

          <Actions>
            <PrimaryOutlineButton
              data-testid={`${DATA_TEST_PRE}Cancel-button`}
              onClick={this.close}
            >
              Cancel
            </PrimaryOutlineButton>
            <PrimaryButton
              data-testid={`${DATA_TEST_PRE}Delete-button`}
              isLoading={this.state.isLoading}
              onClick={this.triggerDelete}
              data-cy={'delete-button'}
            >
              {buttonLabel || 'Delete'}
            </PrimaryButton>
          </Actions>
        </ModalContainer>
      </Modal>
    );
  }
}

const DeleteDoubleCheckModalWithMutations = compose(
  deleteCommunityMutation,
  deleteChannelMutation,
  deleteThreadMutation,
  deleteMessage,
  archiveChannel,
  removeCommunityMember,
  withRouter
)(DeleteDoubleCheckModal);

const map = state => ({
  isOpen: state.modals.isOpen,
  modalProps: state.modals.modalProps,
});

// $FlowIssue
export default connect(map)(DeleteDoubleCheckModalWithMutations);
